import React, { createContext, useContext, useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import { FullScreenSpinner } from 'smoothie';

import { useUser } from '../../user/providers/UserProvider';
import type { User } from '../../user/types/User';
import type { ContainerComponent } from '../types/Common';

const AuthedUserContext = createContext<User | null>(null);

export const useAuthedUser = (): User => {
    const contextValue = useContext(AuthedUserContext);

    if (!contextValue) {
        throw new Error(
            'useAuthedUser must be used within an AuthedUserProvider',
        );
    }

    return contextValue;
};

export const AuthedUserProvider: ContainerComponent = ({ children }) => {
    const { user, loading: isLoadingUser, refetch } = useUser();
    const appState = useRef(AppState.currentState);

    useEffect(() => {
        const subscription = AppState.addEventListener(
            'change',
            (nextAppState) => {
                if (
                    appState.current !== 'active' &&
                    nextAppState === 'active'
                ) {
                    // App has come to foreground! Refetch the user to make sure we have the latest data
                    // Especially helpful if there are updates to user that would affect authorization or
                    // perhaps app experience based on now having a completed guidance session.
                    void refetch?.();
                }

                appState.current = nextAppState;
            },
        );

        return () => {
            subscription.remove();
        };
    }, []);

    if (isLoadingUser) return <FullScreenSpinner />;

    const authedUser = user?.isFullyAuthed ? user : null;

    return (
        <AuthedUserContext.Provider value={authedUser}>
            {children}
        </AuthedUserContext.Provider>
    );
};
